import React, { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { recipesApi } from '@/api/recipes';
import { formatCurrency } from '@/lib/utils';
import type { AppSetting } from '@/types';
import { useAuth } from './AuthProvider';

interface AppSettingsContextValue {
  settings: AppSetting[];
  currencySymbol: string;
  currencyCode: string;
  workHourCost: number;
  getSetting: (key: string, fallback?: string) => string;
}

const AppSettingsContext = createContext<AppSettingsContextValue | null>(null);

export function AppSettingsProvider({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  const { data: settings = [] } = useQuery({
    queryKey: ['settings'],
    queryFn: recipesApi.listSettings,
    enabled: !loading && !!user,
  });

  const getSetting = (key: string, fallback = '') =>
    settings.find(setting => setting.key === key)?.value ?? fallback;

  return (
    <AppSettingsContext.Provider value={{
      settings,
      currencySymbol: getSetting('currency_symbol', '$'),
      currencyCode: getSetting('currency_code', 'USD'),
      workHourCost: parseFloat(getSetting('work_hour_cost', '0')) || 0,
      getSetting,
    }}>
      {children}
    </AppSettingsContext.Provider>
  );
}

export function useAppSettings(): AppSettingsContextValue {
  const context = useContext(AppSettingsContext);
  if (!context) throw new Error('useAppSettings must be used within AppSettingsProvider');
  return context;
}

export function useCurrencyFormatter() {
  const { currencySymbol } = useAppSettings();
  return (value: number, decimals = 2) => formatCurrency(value, currencySymbol, decimals);
}